/**
 * Receipts that could not find their own job.
 *
 * The exception list, and deliberately nothing more. A crew emails a receipt to
 * QuickBooks, Kelsey accepts it there and tags it to the job's project, and it
 * arrives on that job in Roof Board as an other expense without anybody opening
 * this screen (ADR 0089).
 *
 * What lands here is what she tagged to nothing, or to a customer rather than
 * one of their jobs. The shorter this list is, the better the flow is working.
 *
 * Only store purchases on a job reach it at all (ADR 0111). QuickBooks calls
 * rent, fuel, lunches and bank fees Purchases too, and until 2026-09-07 every
 * one of them turned up here.
 */

import {
  formatMoney,
  cents,
  jobChoiceLabel,
  suggestJobs,
} from "@roof-hawk/core";
import { useCallback, useEffect, useMemo, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t } from "../../i18n";
import { fmtDateOnly } from "../../lib/format";
import {
  assignReceiptToJob,
  fetchJobsForReceipt,
  fetchReceiptsNeedingAJob,
  ignoreReceipt,
  receiptImageUrl,
  type QuickbooksReceiptRow,
} from "../../lib/queries";

type JobChoice = Awaited<ReturnType<typeof fetchJobsForReceipt>>[number];

/**
 * The rule, in his words rather than the code's, on both the full list and the
 * empty one — because an empty list that says nothing looks broken.
 */
const whatReachesThisList = () =>
  t(
    "Only purchases with a receipt attached, posted in QuickBooks as a cost of goods sold, from somewhere the company does not hold a credit account. Heely-Brown, ABC, QXO and Central States are invoiced instead, and their materials reach the job as a material order. Overhead — fuel, meals, rent, subscriptions — never appears here.",
  );

export function ReceiptInbox() {
  const [receipts, setReceipts] = useState<QuickbooksReceiptRow[]>([]);
  const [jobs, setJobs] = useState<JobChoice[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [chosen, setChosen] = useState<Record<string, string>>({});

  const refresh = useCallback(async () => {
    const [rows, jobRows] = await Promise.all([
      fetchReceiptsNeedingAJob(),
      fetchJobsForReceipt(),
    ]);
    setReceipts(rows);
    setJobs(jobRows);
  }, []);

  useEffect(() => {
    void refresh().catch((e) => setError(String(e.message ?? e)));
  }, [refresh]);

  async function run(work: () => Promise<void>) {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await work();
      await refresh();
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setBusy(false);
    }
  }

  /** Likely jobs first, then everything else. Nothing is chosen by guessing. */
  const orderedJobs = useMemo(() => {
    const cache = new Map<string, JobChoice[]>();
    return (receipt: QuickbooksReceiptRow) => {
      const hit = cache.get(receipt.id);
      if (hit) return hit;
      const likely = suggestJobs(receipt, jobs);
      const likelyIds = new Set(likely.map((j) => j.id));
      const ordered = [
        ...likely.map((j) => jobs.find((x) => x.id === j.id)!).filter(Boolean),
        ...jobs.filter((j) => !likelyIds.has(j.id)),
      ];
      cache.set(receipt.id, ordered);
      return ordered;
    };
  }, [jobs]);

  async function openReceipt(purchaseId: string) {
    const { url } = await receiptImageUrl(purchaseId);
    if (!url)
      throw new Error(t("That receipt has no image attached in QuickBooks."));
    window.open(url, "_blank", "noopener,noreferrer");
  }

  if (receipts.length === 0) {
    return (
      <div className="block">
        <h3>{t("Receipts")}</h3>
        <div className="muted">
          {t(
            "Nothing waiting. A receipt emailed to QuickBooks and tagged to a job's project lands on that job by itself — only ones tagged to nothing turn up here.",
          )}
        </div>
        <div className="muted">{whatReachesThisList()}</div>
        <ErrorNote error={error} />
      </div>
    );
  }

  return (
    <div className="block">
      <h3>{t("Receipts that need a job")}</h3>
      <div className="muted">
        {t(
          "These were accepted in QuickBooks but not tagged to a job's project, so Roof Board could not tell which job they belong to. Pick one, or set the receipt aside if it was never a job cost.",
        )}
      </div>
      <div className="muted">{whatReachesThisList()}</div>
      <ErrorNote error={error} />
      {note && <div className="muted">{note}</div>}

      <table className="grid">
        <thead>
          <tr>
            <th>{t("Date")}</th>
            <th>{t("Who")}</th>
            <th>{t("Amount")}</th>
            <th>{t("Receipt")}</th>
            <th>{t("Job")}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {receipts.map((row) => (
            <tr key={row.id}>
              <td>{fmtDateOnly(row.txn_date)}</td>
              <td>
                {row.vendor_name ?? row.description ?? "—"}
                {row.account_name && (
                  <div className="muted">{row.account_name}</div>
                )}
              </td>
              <td>
                {row.amount_cents == null
                  ? "—"
                  : formatMoney(cents(Number(row.amount_cents)))}
              </td>
              <td>
                <button
                  disabled={busy}
                  onClick={() =>
                    void run(() => openReceipt(row.quickbooks_purchase_id))
                  }
                >
                  {t("View")}
                </button>
              </td>
              <td>
                <select
                  value={chosen[row.id] ?? ""}
                  onChange={(e) =>
                    setChosen((c) => ({ ...c, [row.id]: e.target.value }))
                  }
                >
                  <option value="">{t("Pick a job…")}</option>
                  {orderedJobs(row).map((job) => (
                    <option key={job.id} value={job.id}>
                      {jobChoiceLabel(job)}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <button
                  disabled={busy || !chosen[row.id]}
                  onClick={() =>
                    void run(async () => {
                      await assignReceiptToJob(row, chosen[row.id]!);
                      setNote(t("On the job, and QuickBooks has been told."));
                    })
                  }
                >
                  {t("Put it on the job")}
                </button>
                <button
                  disabled={busy}
                  onClick={() =>
                    void run(async () => {
                      const why =
                        window.prompt(
                          t(
                            "Set this receipt aside. What was it, if not a job cost?",
                          ),
                        ) ?? "";
                      await ignoreReceipt(row.id, why);
                    })
                  }
                >
                  {t("Not a job cost")}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
